import { useAuthStore } from '@/auth/auth-store';
import { DOCTOR_ROLE } from '@/auth/roles';
import { useProfessionalDetails, usePractice } from '@/settings/services';

/**
 * Resolves the provider signature + clinic stamp + provider name for the
 * SIGNED-IN clinician, to auto-source the provider-signature / clinic-stamp
 * boxes on a document-mode consent form (PROD-1051).
 *
 * Sources (both are existing, persisted fields — no new storage):
 *   - Provider signature → `ProfessionalDetails.doctorSignatureUrl`, captured
 *     on the Settings → Practitioner → Professional Details tab
 *     (`SignatureCaptureField`). Only doctors have a ProfessionalDetails
 *     record, so the fetch is gated on `role === DOCTOR_ROLE` — for an
 *     org-admin / staff signer there is simply no provider signature
 *     (the box stays blank, which the BE treats as non-fatal).
 *   - Clinic stamp → `practiceSetupDetail.practiceStampUrl` for the active
 *     practice, captured on Settings → Practice → Details.
 *
 * Lazy by construction: the only caller is the `ConsentFillSign` wrapper in
 * `documents-chart-tab.tsx`, which is rendered solely inside
 * `{signTemplate && <ConsentFillSign … />}` — i.e. it mounts ONLY while a
 * consent is being signed. So these two GETs fire on demand at sign-time, not
 * on every Docs-tab open. Keep it that way: don't lift this hook to a tab-level
 * or always-mounted parent.
 *
 * Cross-domain note: this consent hook reads the settings GET hooks because
 * the marks live on the practitioner / practice records (settings domain).
 * Kept in one consent-owned file rather than leaking settings hooks into the
 * patients chart call-site. Both underlying queries are `enabled`-guarded on a
 * truthy id, so passing `null` (non-doctor / no practice) is a no-op.
 *
 * The popup converts these URLs → base64 PNG data-URLs at sign time
 * (`imageUrlToDataUrl`) and posts them as `providerSignaturePng` /
 * `clinicStampPng`; the BE never fetches them cross-service (SSRF + the assets
 * live in other storage accounts), so the FE supplies the bytes "by who's
 * using it", exactly like the patient signature PNG.
 */
export interface ProviderConsentMarks {
  providerSignatureUrl: string | null;
  clinicStampUrl: string | null;
  providerName: string | null;
  /** True while the provider's signature (professional details) is still fetching —
   *  lets the popup hold its "no signature on file" error until the URL resolves. */
  providerSignatureLoading: boolean;
}

/**
 * Same marks, but for a SPECIFIED provider (not the signed-in user) — used by the
 * appointment Docs tab, where the treating provider is known from the appointment, so
 * we auto-source THEIR signature rather than the signed-in user's or a manual pick
 * (PROD-1243). `doctorSetupId` is the provider's setup id; on the scheduling wire the
 * appointment participant's `provider.id` IS that doctorSetupId (verified on dev — same
 * id space). Clinic stamp is still the active practice's. `providerSignatureLoading`
 * lets the popup suppress its "no signature" error until the fetch resolves.
 */
export function useConsentMarksForProvider(
  doctorSetupId: string | null,
  providerName: string | null,
): ProviderConsentMarks {
  const practiceId = useAuthStore((s) => s.user?.practiceId ?? null);
  const professional = useProfessionalDetails(doctorSetupId);
  const practice = usePractice(practiceId);
  return {
    providerSignatureUrl: professional.data?.doctorSignatureUrl ?? null,
    clinicStampUrl: practice.data?.practiceSetupDetail?.practiceStampUrl || null,
    providerName,
    providerSignatureLoading: !!doctorSetupId && !!professional.isLoading,
  };
}

export function useProviderConsentMarks(): ProviderConsentMarks {
  const user = useAuthStore((s) => s.user);

  // `user.id` is the doctorSetupId for a doctor (staffSetupId otherwise);
  // only doctors carry a ProfessionalDetails record with a signature.
  const doctorSetupId = user?.role === DOCTOR_ROLE ? (user?.id ?? null) : null;
  const practiceId = user?.practiceId ?? null;

  const professional = useProfessionalDetails(doctorSetupId);
  const practice = usePractice(practiceId);

  const providerName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || null;

  return {
    providerSignatureUrl: professional.data?.doctorSignatureUrl ?? null,
    clinicStampUrl:
      practice.data?.practiceSetupDetail?.practiceStampUrl || null,
    providerName,
    providerSignatureLoading: !!doctorSetupId && !!professional.isLoading,
  };
}
